import React, { useEffect, useState, useMemo } from "react";
import axios from "axios";
import toast from "react-hot-toast";
import Sidebar from "../../GlobalManagement/Pages/Sidebar";
import BackButton from "../../GlobalManagement/Pages/BackButton";
import SearchBar from "../../GlobalManagement/Pages/SearchBar";
import { 
  masterBodyArea, 
  masterContentArea, 
  masterPageShell, 
  masterHeader, 
  masterHeaderCard, 
  masterHeaderInfo, 
  masterHeaderIcon, 
  masterTitle, 
  masterTableBody, 
  masterPanel, 
  masterTable, 
  masterStickyHeadRow, 
  masterTableData, 
  masterTbody, 
  masterTableRow, 
  masterTableCell 
} from "../../GlobalManagement/Pages/masterPageStyles";
import { CheckCircle2 } from "lucide-react";

const formatDDMMYYYY = (dateStr) => {
  if (!dateStr) return "-";
  let d;
  if (dateStr.includes("-") && dateStr.split("-")[0].length === 2) {
    const [day, month, year] = dateStr.split("-");
    d = new Date(year, month - 1, day);
  } else {
    d = new Date(dateStr);
  }
  if (isNaN(d)) return "-";
  return `${String(d.getDate()).padStart(2, "0")}-${String(
    d.getMonth() + 1
  ).padStart(2, "0")}-${d.getFullYear()}`;
};

const HRApprovalAttendaceReq = () => {
  const [requestList, setRequestList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedIds, setSelectedIds] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  useEffect(() => {
    fetchRequests();
  }, []);

  const fetchRequests = async () => {
    try {
      setLoading(true);
      const res = await axios.get("/api/attendance-approval/hr/all");
      setRequestList(res.data || []);
      setSelectedIds([]); 
    } catch (error) {
      toast.error("Failed to load attendance requests for HR");
    } finally {
      setLoading(false);
    }
  };

  const updateRequestStatus = async (id, status) => {
    const actionText = status === "Approved" ? "approve" : "reject";
    if (!window.confirm(`Are you sure you want to ${actionText} this attendance request?`)) {
      return;
    }

    try {
      await axios.put(`/api/attendance-approval/hr/${id}/status`, { status });
      toast.success(`Attendance request ${status} by HR`);
      fetchRequests();
    } catch (error) {
      toast.error("Failed to update status");
    }
  };

  const handleBulkUpdateStatus = async (status) => {
    if (selectedIds.length === 0) {
      toast.error("Please select at least one request.");
      return;
    }

    const actionText = status === "Approved" ? "approve" : "reject";
    if (!window.confirm(`Are you sure you want to ${actionText} ${selectedIds.length} selected request(s)?`)) {
      return;
    }

    try {
      await Promise.all(
        selectedIds.map((id) =>
          axios.put(`/api/attendance-approval/hr/${id}/status`, { status })
        )
      );
      toast.success(`Selected attendance requests ${status} successfully.`);
      fetchRequests();
    } catch (error) {
      toast.error("Failed to update selected requests.");
    }
  };

  const filteredRequests = useMemo(() => {
    const q = searchTerm.toUpperCase().trim();
    if (!q) return requestList;
    return requestList.filter((req) => {
      const empId = String(req.employeeId || "").toUpperCase();
      const userId = String(req.employeeUserId || "").toUpperCase();
      const name = String(req.name || "").toUpperCase();
      return empId.includes(q) || userId.includes(q) || name.includes(q);
    });
  }, [requestList, searchTerm]);

  const paginatedRequests = useMemo(() => {
    if (pageSize === "all") return filteredRequests;
    const start = (currentPage - 1) * pageSize;
    return filteredRequests.slice(start, start + pageSize);
  }, [filteredRequests, currentPage, pageSize]);

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedIds(filteredRequests.map((req) => req._id));
    } else {
      setSelectedIds([]);
    }
  };

  const handleSelectOne = (id) => {
    if (selectedIds.includes(id)) {
      setSelectedIds(selectedIds.filter((item) => item !== id));
    } else {
      setSelectedIds([...selectedIds, id]);
    }
  };
 
  return (
    <div className={masterBodyArea}>
      <Sidebar />
      <div className={`${masterPageShell} ${masterContentArea}`}>
        
        {/* Master Header Style */}
        <header className={masterHeader}>
          <div className={masterHeaderCard}>
            <div className={masterHeaderInfo}>
              <div className={masterHeaderIcon}><CheckCircle2 size={14} /></div>
              <div><h1 className={masterTitle}>HR Attendance Request Approval Panel</h1></div>
            </div>
            <div className="flex gap-2">
              <BackButton />
            </div>
          </div>
        </header>

        <main className={`${masterTableBody} mt-1 w-full flex flex-col flex-1 min-h-0`}>
          {/* Search Bar Panel */}
          <div className={`${masterPanel} p-2 mb-2 border border-slate-200 bg-white`}>
            <SearchBar 
              value={searchTerm} 
              onChange={(val) => {
                setSearchTerm(val);
                setCurrentPage(1);
              }} 
              placeholder="Search by emp ID / user ID / name..." 
              totalItems={filteredRequests.length} 
              currentPage={currentPage} 
              pageSize={pageSize} 
              onPageChange={setCurrentPage} 
              onPageSizeChange={setPageSize} 
            />
          </div>

       <div className="overflow-x-auto w-full">
                      <table className={masterTable}>
                        <thead>
                <tr className={masterStickyHeadRow}>
                  <th className={masterTableData}>S.No</th>
                  <th className={masterTableData}>Employee ID</th>
                  <th className={masterTableData}>Employee Name</th>
                  <th className={masterTableData}>Applied On</th>
                  <th className={masterTableData}>Month / Year</th>
                  <th className={masterTableData}>Absent Req Dates</th>
                  <th className={masterTableData}>Reason</th>
                  <th className={masterTableData}>Statuses</th>
                  <th className={masterTableData}>Action</th>
                </tr>
              </thead>
              <tbody className={masterTbody}>
                {paginatedRequests.length > 0 ? (
                  paginatedRequests.map((req, index) => {
                    const hrDecision = req.HRStatus;
                    const disableApprove = hrDecision === "Approved";
                    const disableReject = hrDecision === "Rejected";
                    const isSelected = selectedIds.includes(req._id);

                    return (
                      <tr key={req._id} className={`${masterTableRow} ${isSelected ? 'bg-blue-50' : ''}`}>
                        <td className={masterTableCell + " text-center"}>
                          {pageSize === "all" ? index + 1 : (currentPage - 1) * pageSize + index + 1}
                        </td>
                        <td className={masterTableCell + " uppercase break-words"}>{req.employeeId}</td>
                        <td className={masterTableCell + " uppercase font-medium break-words"}>{req.name}</td>
                        <td className={masterTableCell}>{formatDDMMYYYY(req.applicationDate)}</td>
                        <td className={masterTableCell}>{req.monthYear}</td>
                        <td className={masterTableCell}>
                          <div className="flex flex-wrap gap-1 justify-center">
                            {req.absentDates?.map((d) => (
                              <span key={d} className="px-1 py-0.5 bg-sky-100 text-sky-800 rounded text-[10px] font-bold border border-sky-200">
                                {formatDDMMYYYY(d)}
                              </span>
                            ))}
                          </div>
                        </td>
                       {/* Fixed Width Reason Column with Natural Cell Wrapping */}
                        <td className={`${masterTableCell} break-words whitespace-normal text-left align-middle`} style={{ width: '15%' }}>
                          <div className="w-full break-words leading-tight">
                            {req.reason || "-"}
                          </div>
                        </td>

                        <td className={masterTableCell + " text-left"}>
                          <div className="flex flex-col">
                            <span className="text-[10px]">
                              RM: <b className="text-green-600">
                                {req.reportingManagerStatus}
                              </b>
                            </span>
                            <span className="text-[10px]">
                              HR: <b className={
                                req.HRStatus === 'Approved' ? 'text-green-600' : 
                                req.HRStatus === 'Rejected' ? 'text-red-600' : 'text-orange-500'
                              }>
                                {req.HRStatus}
                              </b>
                            </span>
                            <span className="text-[10px]">
                              Final: <b className={
                                req.FinalStatus === 'Approved' ? 'text-green-600' : 
                                req.FinalStatus === 'Rejected' ? 'text-red-600' : 'text-orange-500'
                              }>
                                {req.FinalStatus}
                              </b>
                            </span>
                          </div>
                        </td>

                        <td className={masterTableCell}>
                          <div className="flex flex-col items-center gap-1">
                            <div className="flex justify-center gap-1">
                              <button
                                disabled={disableApprove}
                                onClick={() => updateRequestStatus(req._id, "Approved")}
                                className={`px-2 py-[1px] rounded text-[10px] text-white transition ${
                                  disableApprove ? "bg-slate-300 cursor-not-allowed" : "bg-emerald-600 hover:bg-emerald-700"
                                }`}
                              >
                                Approve
                              </button>
                              <button
                                disabled={disableReject}
                                onClick={() => updateRequestStatus(req._id, "Rejected")}
                                className={`px-2 py-[1px] rounded text-[10px] text-white transition ${
                                  disableReject ? "bg-slate-300 cursor-not-allowed" : "bg-rose-600 hover:bg-rose-700"
                                }`}
                              >
                                Reject
                              </button>
                            </div>

                            {hrDecision !== "Pending" && (
                              <span className={`text-[9px] font-bold uppercase ${
                                hrDecision === 'Rejected' ? 'text-rose-600' : 'text-emerald-600'
                              }`}>
                                HR {hrDecision}
                              </span>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan="9" className="text-center py-6 text-slate-400 font-semibold uppercase text-xs">
                      No RM approved attendance requests found for HR review
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </main>
      </div>
    </div>
  );
};

export default HRApprovalAttendaceReq;